/**
 * Mode Registry
 *
 * Central registry of all available training modes with metadata.
 * Used for navigation, UI display, and mode discovery.
 *
 * NOTE: "Simulatie" mode removed — merged with Assessment as "Toets" in Learning Lab (§3.4.12)
 */

import { ModeId, ModeMetadata } from './types'
import { Zap, Target, Trophy, Beaker } from 'lucide-react'

/**
 * Mode metadata registry
 */
export const MODE_REGISTRY: Record<ModeId, ModeMetadata> = {
  fluency: {
    id: 'fluency',
    title: 'Snel & Zeker',
    subtitle: 'Oefen wat je al kent',
    description: 'Oefen sommen die je al kent en word er steeds sneller in!',
    icon: 'Zap',
    route: '/learning-lab/fluency',
    estimatedMinutes: 5,
    difficultyBand: 'Jouw niveau',
    tags: ['Snelheid', 'Herhalen', 'Basis'],
    isEnabled: true,
  },
  sprint: {
    id: 'sprint',
    title: 'Focus Sprint',
    subtitle: 'Extra oefenen',
    description:
      'Oefen extra op onderwerpen waar je nog beter in kunt worden, met handige uitleg erbij.',
    icon: 'Target',
    route: '/learning-lab/sprint',
    estimatedMinutes: 8,
    difficultyBand: 'Waar jij beter in kunt worden',
    tags: ['Gericht', 'Extra oefenen', 'Uitleg'],
    isEnabled: true,
  },
  challenge: {
    id: 'challenge',
    title: 'Uitdaging',
    subtitle: 'Laat zien wat je kunt!',
    description:
      'Klaar voor iets moeilijkers? Probeer lastigere sommen over onderwerpen die je al goed kent.',
    icon: 'Trophy',
    route: '/learning-lab/challenge',
    estimatedMinutes: 5,
    difficultyBand: 'Moeilijker',
    tags: ['Uitdaging', 'Gevorderd'],
    isEnabled: true,
  },
  experiments: {
    id: 'experiments',
    title: 'Experimenten',
    subtitle: 'Ontdek nieuwe vragen',
    description:
      'Ontdek vragen met visuele hulpmiddelen, interactieve opdrachten of nieuwe inhoud die je nog nooit hebt gezien.',
    icon: 'Beaker',
    route: '/learning-lab',
    estimatedMinutes: 10,
    difficultyBand: 'Variabel',
    tags: ['Ontdekken', 'Visueel', 'Interactief'],
    isEnabled: false,
    isBeta: true,
  },
}

/**
 * Get mode metadata by ID
 */
export function getModeMetadata(modeId: ModeId): ModeMetadata {
  return MODE_REGISTRY[modeId]
}

/**
 * Get all enabled modes
 */
export function getEnabledModes(): ModeMetadata[] {
  return Object.values(MODE_REGISTRY).filter((mode) => mode.isEnabled)
}

/**
 * Get icon component for mode
 */
export function getModeIcon(modeId: ModeId) {
  const iconMap = {
    fluency: Zap,
    sprint: Target,
    challenge: Trophy,
    experiments: Beaker,
  }
  return iconMap[modeId]
}
